import { RESTARTED_QUESTION_ID } from "@/utils/restartRealtime";

/**
 * Helpers for Questionnaire Details → Sections tab response filtering.
 */

export type DetailsResponseRow = {
  sectionId?: number | string | null;
  field?: string | number | null;
  value?: string | number | null;
  response?: string | number | null;
};

export type DetailsRealtimeRow = {
  id?: number | null;
  sectionId?: number | string | null;
  question?: string | null;
  clientTime?: string | Date | null;
  time?: string | Date | null;
};

export function responseSectionId(row: DetailsResponseRow | null | undefined): number {
  return Number(row?.sectionId ?? 0);
}

export function responseDisplayValue(row: DetailsResponseRow | null | undefined): string {
  if (row?.value !== null && row?.value !== undefined && String(row.value) !== '') {
    return String(row.value);
  }
  if (row?.response !== null && row?.response !== undefined && String(row.response) !== '') {
    return String(row.response);
  }
  return String(row?.value ?? '');
}

export function filterResponsesForSection(
  responses: DetailsResponseRow[] | null | undefined,
  selectedSectionId: number | string | null | undefined
): DetailsResponseRow[] {
  const list = Array.isArray(responses) ? responses : [];
  const selected = Number(selectedSectionId ?? 0);
  if (!selected) return list;
  return list.filter((r) => responseSectionId(r) === selected);
}

/** Prefer the first section that actually has persisted Responses. */
export function firstSectionIdWithResponses(
  sections: Array<{ sectionId?: number | string | null }> | null | undefined,
  responses: DetailsResponseRow[] | null | undefined
): number | null {
  const list = Array.isArray(responses) ? responses : [];
  const sectionIdsWithData = new Set(list.map(responseSectionId).filter((id) => id > 0));
  if (sectionIdsWithData.size === 0) return null;

  const ordered = Array.isArray(sections) ? sections : [];
  for (const section of ordered) {
    const id = Number(section?.sectionId ?? 0);
    if (id > 0 && sectionIdsWithData.has(id)) return id;
  }

  return [...sectionIdsWithData][0] ?? null;
}

function realtimeEventTimeMs(row: DetailsRealtimeRow | null | undefined): number {
  const raw = row?.clientTime || row?.time;
  if (!raw) return 0;
  if (raw instanceof Date) {
    const ms = raw.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : 0;
}

/** Taker action time (ClientTime), then server EnterTime, then Id. */
export function sortRealtimeResponsesForDisplay<T extends DetailsRealtimeRow>(
  realtimeResponses: T[] | null | undefined
): T[] {
  const list = Array.isArray(realtimeResponses) ? [...realtimeResponses] : [];
  list.sort((a, b) => {
    const timeDelta = realtimeEventTimeMs(a) - realtimeEventTimeMs(b);
    if (timeDelta !== 0) return timeDelta;
    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
  return list;
}

export function countRestartedRealtimeForSection(
  realtimeResponses: DetailsRealtimeRow[] | null | undefined,
  sectionId: number | string | null | undefined
): number {
  const list = Array.isArray(realtimeResponses) ? realtimeResponses : [];
  const selected = Number(sectionId ?? 0);
  if (!selected) return 0;
  const restarted = RESTARTED_QUESTION_ID.toLowerCase();
  return list.filter((row) => {
    if (responseSectionId(row) !== selected) return false;
    return String(row?.question || "").trim().toLowerCase() === restarted;
  }).length;
}
